import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, ProgressBar } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { ChargingBASE_URL } from '../../Config';
import { BASE_URL } from '../../Config';


const ReportByNo_CustomerService = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [msisdn, setMsisdn] = useState("");
  const [userId, setUserId] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // State for the search query

  // Get UserId from session storage on component mount
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (user) {
      setUserId(user.userId);
    }
  }, []);

  // Handle the reversal POST request
  const handleReversal = async (transactionId, id) => {
    if (!userId || !transactionId || !id) return;

    const url = `${BASE_URL}JazzCash_Reversal`;
    const requestData = {
      userId: userId,
      originalTransactionId: transactionId,
      id: id, 
      mobileNo: msisdn
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
      } else {
        throw new Error("Reversal request failed");
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const columns = [
    { name: "Id", selector: (row) => row.id, sortable: true },
    { name: "Agent Name", selector: (row) => row.agentName, sortable: true },
    { name: "Company Name", selector: (row) => row.companyName, sortable: true },
    { name: "Timestamp", selector: (row) => row.timestamp, sortable: true },
    { name: "Reason", selector: (row) => row.reason, sortable: true },
    { name: "Amount", selector: (row) => row.amount, sortable: true },
    { name: "Account Number", selector: (row) => row.account_Number, sortable: true },
    { name: "Status", selector: (row) => row.status, sortable: true },
    { name: "Transaction ID", selector: (row) => row.transactionId, sortable: true },
    {
      name: "Action",
      cell: (row) => {
        if (row.status === "Sub") {
          return (
            <Button variant="danger" onClick={() => handleReversal(row.transactionId, row.id)} > Reversal </Button>
          );
        }
        // If the status is not "Success", return null (i.e., no button)
        return null;
      },
      sortable: false,
    },
  ];


  // Fetch data from API
  const fetchData = async () => {
    if (!msisdn) {
      setError("MSISDN is required");
      return;
    }

    const url = new URL(`${ChargingBASE_URL}ReportByNo`);
    const params = { UserId: userId, Msisdn: msisdn };
    url.search = new URLSearchParams(params).toString();

    try {
      setIsLoading(true);
      setProgress(1); // Start progress at 1%

      // Simulate the progress bar loader
      const progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prevProgress + 5;
        });
      }, 100);

      const response = await fetch(url, { method: "GET", headers: { Accept: "*/*" } });

      if (!response.ok) throw new Error("Failed to fetch data");

      const result = await response.json();
      setData(result); // Set the fetched data
      setFilteredData(result); // Set the filtered data initially to show all
      setError(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  // Handle search filter input
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query === "") {
      setFilteredData(data); // Show all data when search query is empty
    } else {
      const filtered = data.filter((row) =>
        Object.values(row).some((value) => value.toString().toLowerCase().includes(query.toLowerCase()))
      );
      setFilteredData(filtered);
    }
  };

  return (
    <>
      {/* Show loading progress bar if data is being fetched */}

      <Row className="justify-content-center">
        <Col lg={12} md={12} sm={12}>
          <Card className="mt-1 shadow-lg">            
            <Card.Body>
              {/* Form for MSISDN input */}
              <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md={4} sm={6}><b>Please Enter the MobileNo: </b></Col>
                  <Col md={4} sm={6}>
                    <Form.Group controlId="msisdn">
                      <Form.Control
                        type="text"
                        placeholder="Enter MSISDN"
                        value={msisdn}
                        onChange={(e) => setMsisdn(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4} sm={6}>
                    <Button variant="primary" type="submit" className="w-100" disabled={isLoading}>
                      {isLoading ? "Loading..." : "Search"}
                    </Button>
                  </Col>
                </Row>
              </Form>
              {isLoading && (
                <div className="text-center mt-2">
                  <ProgressBar animated now={progress} label={`Loading Data....${progress}%`} />
                </div>
              )}

              {/* Error handling */}
              {error && <div className="text-center mt-2">{error}</div>}

              {/* DataTable for displaying the fetched data */}
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                responsive
                highlightOnHover
                striped
                dense
                paginationPerPage={5}
                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                defaultSortFieldId={1} // Default sort column (Timestamp)
                defaultSortAsc={true} // Default sort order (ascending)
              />

              {/* Download Button appears only after the data is successfully fetched */}
              {filteredData.length > 0 && !isLoading && (
                <CSVLink
                  data={filteredData}
                  filename={"transaction_report.csv"}
                  className="btn btn-success mt-1 w-100"
                >
                  Download CSV
                </CSVLink>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ReportByNo_CustomerService;
